import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {add, deleteRecord, fetch, update} from '../services/BankAccountsService';
import {BankAccount} from '../types';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications';

export const useBankAccount = (customerId: string, bankAccountId?: string) => {
    const queryClient = useQueryClient();

    const bankAccountQuery = useQuery({
        queryKey: ['bankAccount', customerId, bankAccountId],
        queryFn: () => fetch(customerId, bankAccountId!),
        enabled: !!bankAccountId,
    });

    const createMutation = useMutation({
        mutationFn: (values: BankAccount) => {
            const payload = {...values} as Record<string, unknown>;
            delete payload.created;
            delete payload.updated;
            return add(payload as BankAccount);
        },
        onSuccess: () => {
            notifySuccess('Bank account created');
            queryClient.invalidateQueries({queryKey: ['bankAccounts', customerId]});
        },
        onError: notifyError,
    });

    const updateMutation = useMutation({
        mutationFn: (values: BankAccount) => {
            if (!bankAccountId) throw new Error('bankAccountId is required parameter');
            const payload = {...values} as Record<string, unknown>;
            delete payload.created;
            delete payload.updated;
            return update(payload as BankAccount);
        },
        onSuccess: () => {
            notifySuccess('Bank account updated');
            queryClient.invalidateQueries({queryKey: ['bankAccounts', customerId]});
            queryClient.invalidateQueries({queryKey: ['bankAccount', customerId, bankAccountId]});
        },
        onError: notifyError,
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!bankAccountId) throw new Error('bankAccountId is required parameter');
            return deleteRecord(customerId, bankAccountId);
        },
        onSuccess: () => {
            notifySuccess('Bank account deleted');
            queryClient.invalidateQueries({queryKey: ['bankAccounts', customerId]});
        },
        onError: notifyError,
    });

    return {
        record: bankAccountQuery.data,
        loading: bankAccountQuery.isLoading,
        fetchRecord: bankAccountQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation,
    };
};
